/**
 * Contact Intelligence Scoring Engine v2 - CORRECTED
 * Evidence-based scoring with proper decay functions
 * 
 * Key improvements:
 * - Uses actual database tables (emails, calendarEvents, messagesEnhanced)
 * - Corrected field names from schema
 * - Proper type handling for SQLite boolean strings
 * - Safe null handling throughout
 */

import { blink } from '@/lib/blink';
import { scoreCache } from '@/lib/scoreCache';
import { withRateLimitHandling } from '@/lib/rateLimitHandler';
import { safeJsonParse } from '@/lib/utils';

// ============================================================================
// Constants & Config
// ============================================================================

export const HALF_LIFE = {
  email: 21,
  meeting: 28,
  call: 21,
  dm: 10,
} as const;

export const CHANNEL_WEIGHT = {
  email: 1.0,
  meeting: 2.5,
  call: 2.0,
  dm: 0.7,
} as const;

// ============================================================================
// Core Math Functions
// ============================================================================

/**
 * Decay function: exp(-ln(2) * days / half_life)
 */
export const decay = (days: number, halfLife: number): number => {
  if (days < 0) return 1;
  return Math.exp(-Math.log(2) * days / halfLife);
};

/**
 * Map sentiment from [-1..1] to [0..1]
 */
export const mapSentiment01 = (s: number): number => (s + 1) / 2;

// ============================================================================
// Type Definitions
// ============================================================================

export interface Touchpoint {
  tpId: string;
  contactId: string;
  source: 'gmail' | 'calendar' | 'call' | 'dm';
  direction: 'inbound' | 'outbound' | 'meeting';
  startedAt: string;
  endedAt?: string;
  metadata: {
    subject?: string;
    attendees?: string[];
    duration?: number;
    messageId?: string;
  };
  extracted?: {
    sentiment: number;
    intent: string;
    asks: string[];
    offers: string[];
    tokens: number;
    topics: string[];
  };
}

export interface ContactFeatures {
  recency: number;
  frequency: number;
  depth: number;
  sentiment: number;
  icpFit: number;
  timing: number;
  budget: number;
  urgency: number;
  seniority: number;
  centrality: number;
  bridge: number;
  responseTime: number;
  reliability: number;
  reciprocity: number;
  trustRiskMod: number;
}

export interface CIScore {
  strength: number;
  relevance: number;
  influence: number;
  responsiveness: number;
  reciprocity: number;
  ci: number;
  tier: 'A' | 'B' | 'C';
}

export interface ContactCard {
  contactId: string;
  email: string;
  name?: string;
  scores: CIScore;
  lastTouchAt?: string;
  nextTouchDueAt?: string;
  whyNow: string[];
  openLoops: string[];
  sparkline: number[];
}

// ============================================================================
// Feature Computation (0..1 normalized)
// ============================================================================

/**
 * Recency: days since last touch with configurable half-life
 */
export function recency01(daysSinceLastTouch: number, hl = 21): number {
  return daysSinceLastTouch < 0 ? 1 : decay(daysSinceLastTouch, hl);
}

/**
 * Frequency: decayed touch count over expected rate
 */
export function frequency01Decayed(
  tps: Touchpoint[], 
  expectedPer30 = 4
): number {
  const now = Date.now();
  let wt = 0;
  
  for (const t of tps) {
    const ageD = (now - new Date(t.startedAt).getTime()) / 86400000;
    const hl = t.source === 'calendar' ? HALF_LIFE.meeting : HALF_LIFE.email;
    wt += decay(ageD, hl);
  }
  
  return Math.min(wt / expectedPer30, 1);
}

/**
 * Depth: content richness + meeting depth with decay weighting
 */
export function depth01(tps: Touchpoint[]): number {
  const now = Date.now();
  let s = 0, w = 0;
  
  for (const t of tps) {
    const ageD = (now - new Date(t.startedAt).getTime()) / 86400000;
    const hl = t.source === 'calendar' ? HALF_LIFE.meeting : HALF_LIFE.email;
    const d = decay(ageD, hl);
    
    const content = Math.min((t.extracted?.tokens ?? 0) / 1200, 1);
    const meeting = t.source === 'calendar' 
      ? Math.min((t.metadata.duration ?? 0) / 60, 1) 
      : 0;
    const richness = t.source === 'calendar' 
      ? 0.6 * meeting + 0.4 * content 
      : content;
    
    s += d * richness;
    w += d;
  }
  
  return w ? Math.min(s / w, 1) : 0;
}

/**
 * Response speed: continuous curve, capped at 7 days
 */
export function speed01(medianHours: number): number {
  const h = Math.max(0, Math.min(168, medianHours));
  return 1 - (h / 168);
}

/**
 * Reply rate: % of our outbound that got their first reply
 */
export function replyRate01(tps: Touchpoint[]): number {
  let got = 0, need = 0;
  
  for (let i = 0; i < tps.length; i++) {
    if (tps[i].direction === 'outbound') {
      need++;
      for (let j = i + 1; j < tps.length; j++) {
        if (tps[j].direction === 'inbound') {
          got++;
          break;
        }
      }
    }
  }
  
  return need ? Math.min(got / need, 1) : 0.5;
}

/**
 * Bridge: distinct org domains from participant emails
 */
function distinctOrgDomainsFromParticipants(tps: Touchpoint[]): number {
  const domains = new Set<string>();
  
  for (const t of tps) {
    (t.metadata.attendees ?? []).forEach(email => {
      const m = String(email).match(/@([\w.-]+\.\w+)/);
      if (m) domains.add(m[1].toLowerCase());
    });
  }
  
  return domains.size;
}

/**
 * Centrality proxy: distinct people engaged
 */
function centralityProxy(tps: Touchpoint[]): number {
  const people = new Set<string>();
  (tps || []).forEach(t => 
    (t.metadata.attendees ?? []).forEach(a => people.add(a))
  );
  return Math.min(people.size / 8, 1);
}

/**
 * Seniority from title/role keywords
 */
function inferSeniority(contact: any): number {
  if (!contact?.name) return 0.5;
  
  const lower = contact.name.toLowerCase();
  
  if (/\b(ceo|cto|cfo|coo|founder|partner|president|vp|vice president)\b/.test(lower)) {
    return 0.9;
  }
  if (/\b(director|head of|chief)\b/.test(lower)) {
    return 0.7;
  }
  if (/\b(manager|lead|senior)\b/.test(lower)) {
    return 0.6;
  }
  
  return 0.5;
}

// ============================================================================
// Scoring Functions
// ============================================================================

/**
 * Strength: geometric mean of R, F, D, S
 */
export function strengthScore(
  R01: number, 
  F01: number, 
  D01: number, 
  S01: number
): number {
  const product = Math.max(R01, 0) * Math.max(F01, 0) * Math.max(D01, 0) * Math.max(S01, 0);
  if (product <= 0) return 0;
  const geomMean = Math.pow(product, 0.25);
  return Math.round(100 * geomMean);
}

/**
 * Relevance: 0.5*ICP + 0.2*Timing + 0.2*Budget + 0.1*Urgency
 */
export function relevanceScore(
  icpFit: number,
  timing: number,
  budget: number,
  urgency: number
): number {
  const weighted = 0.5 * icpFit + 0.2 * timing + 0.2 * budget + 0.1 * urgency;
  return Math.round(100 * weighted);
}

/**
 * Influence: 0.4*Seniority + 0.3*Centrality + 0.3*Bridge
 */
export function influenceScore(
  seniority: number,
  centrality: number,
  bridge: number
): number {
  const weighted = 0.4 * seniority + 0.3 * centrality + 0.3 * bridge;
  return Math.round(100 * weighted);
}

/**
 * Responsiveness: 0.7*Speed + 0.3*ReplyRate
 */
export function responsivenessScore(
  speed: number,
  replyRate: number
): number {
  const weighted = 0.7 * speed + 0.3 * replyRate;
  return Math.round(100 * weighted);
}

/**
 * Composite CI: weighted sum with optional modifier
 */
export function compositeCI(
  strength: number,
  relevance: number,
  influence: number,
  responsiveness: number,
  reciprocity: number,
  modifier = 1.0
): number {
  const ciRaw =
    0.35 * strength +
    0.25 * relevance +
    0.15 * influence +
    0.15 * responsiveness +
    0.10 * reciprocity;
  return Math.round(ciRaw * modifier);
}

/**
 * Tier assignment
 */
export function assignTier(ciScore: number): 'A' | 'B' | 'C' {
  if (ciScore >= 80) return 'A';
  if (ciScore >= 60) return 'B';
  return 'C';
}

// ============================================================================
// Main Pipeline
// ============================================================================

/**
 * Calculate complete CI score for a contact
 */
export async function calculateCIScoreV2(
  userId: string,
  contactId: string,
  useCache: boolean = true
): Promise<{ features: ContactFeatures; scores: CIScore; card: ContactCard }> {
  // Check cache first
  if (useCache) {
    const cached = scoreCache.get<{ features: ContactFeatures; scores: CIScore; card: ContactCard }>('ci', userId, contactId);
    if (cached) {
      console.log(`[CI Cache] HIT for contact ${contactId}`);
      return cached;
    }
    console.log(`[CI Cache] MISS for contact ${contactId}`);
  }

  try {
    // Fetch contact first to get email with rate limit handling
    const contacts = await withRateLimitHandling(
      () => blink.db.contacts.list({
        where: { id: contactId, userId },
        limit: 1
      } as any) || [],
      `ci-contact-${userId}-${contactId}`,
      { maxRetries: 2, initialDelay: 2000 }
    );

    if (contacts.length === 0) {
      throw new Error(`Contact ${contactId} not found`);
    }

    const contact = contacts[0];
    const contactEmail = contact.email || '';

    // Fetch emails and meetings using actual tables with rate limit handling
    let emails: any[] = [];
    let meetings: any[] = [];
    
    // Try messagesEnhanced first (if exists), then fall back to emails
    try {
      const me = (blink.db as any).messagesEnhanced;
      if (me && typeof me.list === 'function') {
        emails = (await withRateLimitHandling(
          () => me.list({
            where: { userId },
            orderBy: { receivedDate: 'desc' },
            limit: 100,
          }),
          `ci-messages-${userId}`,
          { maxRetries: 2, initialDelay: 2000 }
        )) || [];
      }
    } catch (e) {
      console.warn('messagesEnhanced not available, trying emails:', e);
    }
    
    // Fall back to emails table
    if (emails.length === 0) {
      try {
        emails = (await withRateLimitHandling(
          () => blink.db.emails.list({
            where: { userId },
            orderBy: { createdAt: 'desc' },
            limit: 100,
          } as any),
          `ci-emails-${userId}`,
          { maxRetries: 2, initialDelay: 2000 }
        )) || [];
      } catch (e2) {
        console.warn('emails query failed:', e2);
        emails = [];
      }
    }
    
    // Try calendarEventsEnhanced first (if exists), then fall back to calendarEvents
    try {
      const ce = (blink.db as any).calendarEventsEnhanced;
      if (ce && typeof ce.list === 'function') {
        meetings = (await withRateLimitHandling(
          () => ce.list({
            where: { userId },
            orderBy: { startTime: 'desc' },
            limit: 50,
          }),
          `ci-cal-enhanced-${userId}`,
          { maxRetries: 2, initialDelay: 2000 }
        )) || [];
      }
    } catch (e) {
      console.warn('calendarEventsEnhanced not available, trying calendarEvents:', e);
    }
    
    // Fall back to calendarEvents table
    if (meetings.length === 0) {
      try {
        meetings = (await withRateLimitHandling(
          () => blink.db.calendarEvents.list({
            where: { userId },
            orderBy: { createdAt: 'desc' },
            limit: 50,
          } as any),
          `ci-cal-${userId}`,
          { maxRetries: 2, initialDelay: 2000 }
        )) || [];
      } catch (e2) {
        console.warn('calendarEvents query failed:', e2);
        meetings = [];
      }
    }

    // Filter emails related to this contact
    const contactEmails = emails.filter(e => {
      const senderEmail = (e.senderEmail || e.sender_email || '').toLowerCase();
      const toRecipients = e.toRecipients || e.to_recipients || '';
      const ccRecipients = e.ccRecipients || e.cc_recipients || '';
      const bccRecipients = e.bccRecipients || e.bcc_recipients || '';
      
      return senderEmail === contactEmail.toLowerCase() ||
             toRecipients.toLowerCase().includes(contactEmail.toLowerCase()) ||
             ccRecipients.toLowerCase().includes(contactEmail.toLowerCase()) ||
             bccRecipients.toLowerCase().includes(contactEmail.toLowerCase());
    });

    // Convert to touchpoint format
    const touchpoints: Touchpoint[] = [
      ...contactEmails.map(email => ({
        tpId: email.id,
        contactId,
        source: 'gmail' as const,
        direction: Number(email.isSent || email.is_sent) > 0 ? 'outbound' as const : 'inbound' as const,
        startedAt: email.receivedDate || email.received_date || email.sentDate || email.sent_date || new Date().toISOString(),
        metadata: {
          subject: email.subject,
          messageId: email.providerMessageId || email.provider_message_id,
        },
        extracted: {
          sentiment: 0,
          intent: 'unknown',
          asks: [],
          offers: [],
          tokens: (email.bodyText || email.body_text || '').length,
          topics: [],
        },
      })),
      ...meetings.map(meeting => ({
        tpId: meeting.id,
        contactId,
        source: 'calendar' as const,
        direction: 'meeting' as const,
        startedAt: meeting.startTime || meeting.start_time || new Date().toISOString(),
        endedAt: meeting.endTime || meeting.end_time,
        metadata: {
          subject: meeting.title,
          attendees: meeting.attendees 
            ? (typeof meeting.attendees === 'string' ? safeJsonParse(meeting.attendees) : meeting.attendees)
            : [],
          duration: meeting.startTime && meeting.endTime 
            ? (new Date(meeting.endTime).getTime() - new Date(meeting.startTime).getTime()) / 60000
            : (meeting.start_time && meeting.end_time
              ? (new Date(meeting.end_time).getTime() - new Date(meeting.start_time).getTime()) / 60000
              : 0),
        },
        extracted: {
          sentiment: 0.7,
          intent: 'meeting',
          asks: [],
          offers: [],
          tokens: (meeting.description || '').length,
          topics: [],
        },
      })),
    ];

    // Sort by time DESC
    touchpoints.sort((a, b) => 
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );

    const now = Date.now();
    const lastTouch = touchpoints.length > 0 
      ? new Date(touchpoints[0].startedAt).getTime() 
      : null;
    const daysSinceLastTouch = lastTouch 
      ? (now - lastTouch) / 86400000 
      : 999;

    // Compute features
    const recency = recency01(daysSinceLastTouch);
    const frequency = frequency01Decayed(touchpoints, 4);
    const depth = depth01(touchpoints);
    
    const avgSentiment = touchpoints.length > 0
      ? touchpoints.reduce((sum, tp) => sum + (tp.extracted?.sentiment || 0), 0) / touchpoints.length
      : 0;
    const sentiment = mapSentiment01(avgSentiment);

    let icpFit = 0.5;
    if (contact) {
      if (contact.relationshipType === 'investor' || contact.relationshipType === 'strategic_partner') {
        icpFit += 0.3;
      } else if (contact.relationshipType === 'customer' || contact.relationshipType === 'prospect') {
        icpFit += 0.2;
      }
      if (contact.relationshipStage === 'active' || contact.relationshipStage === 'engaged') {
        icpFit += 0.2;
      }
      icpFit = Math.min(icpFit, 1.0);
    }

    const timing = daysSinceLastTouch <= 7 ? 0.95 
      : daysSinceLastTouch <= 14 ? 0.8 
      : daysSinceLastTouch <= 30 ? 0.6 
      : 0.3;

    const budget = 0.5;
    const urgency = 0.3;

    const seniority = inferSeniority(contact);
    const centrality = centralityProxy(touchpoints);
    const bridge = Math.min(distinctOrgDomainsFromParticipants(touchpoints) / 5, 1);

    // Response metrics
    let totalResponseTime = 0;
    let responseCount = 0;
    for (let i = 0; i < touchpoints.length - 1; i++) {
      if (touchpoints[i].direction === 'inbound' && touchpoints[i + 1].direction === 'outbound') {
        const timeDiff = new Date(touchpoints[i + 1].startedAt).getTime() - 
                         new Date(touchpoints[i].startedAt).getTime();
        totalResponseTime += timeDiff / (1000 * 60 * 60);
        responseCount++;
      }
    }
    const avgResponseHours = responseCount > 0 ? totalResponseTime / responseCount : 24;
    const responseTime = speed01(avgResponseHours);
    const reliability = replyRate01(touchpoints);

    const reciprocity = 0.5;
    const trustRiskMod = 1.0;

    const features: ContactFeatures = {
      recency,
      frequency,
      depth,
      sentiment,
      icpFit,
      timing,
      budget,
      urgency,
      seniority,
      centrality,
      bridge,
      responseTime,
      reliability,
      reciprocity,
      trustRiskMod,
    };

    // Calculate scores
    const strength = strengthScore(recency, frequency, depth, sentiment);
    const relevance = relevanceScore(icpFit, timing, budget, urgency);
    const influence = influenceScore(seniority, centrality, bridge);
    const responsiveness = responsivenessScore(responseTime, reliability);
    const reciprocityScore = Math.round(100 * reciprocity);
    const ci = compositeCI(strength, relevance, influence, responsiveness, reciprocityScore, trustRiskMod);
    const tier = assignTier(ci);

    const scores: CIScore = {
      strength,
      relevance,
      influence,
      responsiveness,
      reciprocity: reciprocityScore,
      ci,
      tier,
    };

    // Generate why-now
    const whyNow: string[] = [];
    if (recency > 0.8) whyNow.push('Recent activity - last interaction within 7 days');
    else if (recency < 0.3) whyNow.push('Relationship cooling - no contact for 60+ days');
    if (timing > 0.8) whyNow.push('High timing relevance - active in current window');
    if (icpFit > 0.8) whyNow.push('Strong ICP match - strategic fit');
    if (responseTime > 0.9) whyNow.push('Very responsive - typically replies within 1 hour');
    if (seniority > 0.8) whyNow.push('High seniority contact - executive level influence');
    if (whyNow.length === 0) whyNow.push('Steady relationship - maintain regular cadence');

    // Open loops
    const openLoops: string[] = [];

    // Sparkline (last 30 days)
    const sparkline: number[] = [];
    for (let i = 0; i < 30; i++) {
      const dayStart = now - ((29 - i) * 86400000);
      const dayEnd = dayStart + 86400000;
      const dayTps = touchpoints.filter(tp => {
        const t = new Date(tp.startedAt).getTime();
        return t >= dayStart && t < dayEnd;
      });
      sparkline.push(dayTps.length * 10 + 60);
    }

    const card: ContactCard = {
      contactId,
      email: contact.email || '',
      name: contact.name,
      scores,
      lastTouchAt: touchpoints[0]?.startedAt,
      nextTouchDueAt: calculateNextTouchWindow(touchpoints[0]?.startedAt, tier),
      whyNow: whyNow.slice(0, 3),
      openLoops: openLoops.slice(0, 3),
      sparkline,
    };

    const result = { features, scores, card };

    // Cache the result
    if (useCache) {
      scoreCache.set('ci', userId, contactId, result);
      console.log(`[CI Cache] SET for contact ${contactId}`);
    }

    return result;
  } catch (error) {
    console.error('Failed to calculate CI score:', error);
    return {
      features: {
        recency: 0,
        frequency: 0,
        depth: 0,
        sentiment: 0.5,
        icpFit: 0.5,
        timing: 0,
        budget: 0,
        urgency: 0,
        seniority: 0.5,
        centrality: 0,
        bridge: 0,
        responseTime: 0,
        reliability: 0,
        reciprocity: 0,
        trustRiskMod: 1.0,
      },
      scores: {
        strength: 50,
        relevance: 50,
        influence: 50,
        responsiveness: 50,
        reciprocity: 50,
        ci: 50,
        tier: 'C',
      },
      card: {
        contactId,
        email: '',
        scores: {
          strength: 50,
          relevance: 50,
          influence: 50,
          responsiveness: 50,
          reciprocity: 50,
          ci: 50,
          tier: 'C',
        },
        whyNow: ['Unable to calculate scores'],
        openLoops: [],
        sparkline: Array(30).fill(60),
      },
    };
  }
}

function calculateNextTouchWindow(lastTouch: string | undefined, tier: 'A' | 'B' | 'C'): string {
  if (!lastTouch) return new Date().toISOString();
  
  const cadenceDays = { A: 7, B: 14, C: 30 };
  const days = cadenceDays[tier];
  const nextDate = new Date(lastTouch);
  nextDate.setDate(nextDate.getDate() + days);
  
  return nextDate.toISOString();
}
